'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useUIModalStore } from '../../stores/uiModalStore';
import { needsBillingRecovery } from '../../lib/billingRecovery';
import { trackEvent } from '../../lib/analytics';
import { detectLang } from '../../hooks/useLanguage';
import { BILLING_RECOVERY_COPY, billingRecoveryCopy } from '../../lib/billingRecoveryCopy';
import './BillingRecoveryBanner.css';


function isDismissed(key: string): boolean {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}

export default function BillingRecoveryBanner() {
  const user = useAuthStore((s) => s.user);
  const subscription = useSubscriptionStore((s) => s.subscription);
  const createPortalSession = useSubscriptionStore((s) => s.createPortalSession);
  const modalOpen = useUIModalStore((s) => s.authModalOpen || s.pricingModalOpen);
  const [dismissedKey, setDismissedKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState('');
  const inFlight = useRef(false);
  const shownKey = useRef('');
  const eligible = !!user && subscription?.user_id === user.id && needsBillingRecovery(subscription);
  const key = eligible ? `la-billing-recovery:${user!.id}:${subscription!.stripe_subscription_id}:${subscription!.status}:${subscription!.current_period_end}` : '';
  const visible = !!key && key !== dismissedKey && !isDismissed(key) && !modalOpen;

  useEffect(() => {
    if (!visible || shownKey.current === key) return;
    shownKey.current = key;
    trackEvent('billing_recovery_shown', { status: subscription?.status });
  }, [visible, key, subscription?.status]);

  if (!visible) return null;
  // Providers is outside LanguageProvider; the page's explicit locale wins.
  const pathLang = window.location.pathname.split('/')[1];
  const copy = billingRecoveryCopy(pathLang in BILLING_RECOVERY_COPY ? pathLang : detectLang());

  const openBilling = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setErrorKey('');
    trackEvent('billing_recovery_click', { status: subscription?.status });
    try {
      const url = await createPortalSession();
      if (!url) throw new Error('Billing unavailable');
      trackEvent('billing_recovery_opened', { status: subscription?.status });
      window.location.assign(url);
    } catch {
      setErrorKey(key);
      trackEvent('billing_recovery_error', { status: subscription?.status });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <aside className="billing-recovery" aria-label={copy.title}>
      <div className="billing-recovery__content">
        <strong>{copy.title}</strong>
        <p>{copy.detail}</p>
        {errorKey === key && <p className="billing-recovery__error" role="alert">{copy.error}</p>}
        <button className="billing-recovery__cta" type="button" onClick={openBilling} disabled={busy}>
          {busy ? copy.busy : copy.cta}
        </button>
      </div>
      <button className="billing-recovery__dismiss" type="button" aria-label={copy.dismiss} onClick={() => {
        setDismissedKey(key);
        try { sessionStorage.setItem(key, '1'); } catch { /* Dismiss still works in memory. */ }
        trackEvent('billing_recovery_dismissed', { status: subscription?.status });
      }}>×</button>
    </aside>
  );
}
